// The verdict, and what it stands on.
//
// Layout follows CLAUDE.md "Evidence before judgement": the card leads with
// what the evaluation rests on and what it could not see, then the
// judgement, then context. Each block is labelled as one of the three so a
// reader can tell an admission ("first crack is a recipe stamp, so DTR is
// plan-relative") from a judgement ("development within your band").
//
// Nothing here recomputes a number. Every sentence is the backend's own,
// and every table cell is a field off `Evaluation.to_dict()`.

import { Link } from "react-router-dom";
import type {
  DevelopmentAssessment,
  Evaluation,
  EvaluationContext,
  PlanAdherence,
  TargetDelta,
} from "../types";
import { milestoneName, sourceLabel } from "../chart";
import { formatConfidence, formatTime } from "../format";

export function VerdictCard({
  ev,
  roastId,
}: {
  ev: Evaluation;
  roastId: string;
}) {
  const evidence = ev.evidence;
  // What kind of sentence the headline is. A headline built with no
  // assessable development and no evaluated defects is an admission —
  // "could not judge" — however it is phrased.
  const isJudgement =
    evidence != null &&
    (evidence.development_assessable || evidence.defects_evaluated);
  const limitations = evidence?.limitations ?? [];

  return (
    <div className="card verdict">
      <div className="row" style={{ alignItems: "baseline" }}>
        <h2 style={{ margin: 0 }}>Verdict</h2>
        <span className={`badge ${isJudgement ? "" : "warn"}`}>
          {isJudgement ? "judgement" : "admission — could not fully assess"}
        </span>
        {ev.dtr_is_plan_relative && (
          <span className="badge warn" title="First crack is a recipe stamp, so DTR measures time after the scheduled mark — not after audible first crack.">
            DTR is plan-relative
          </span>
        )}
      </div>

      <p className="verdict-headline">{ev.headline}</p>
      <p className="muted" style={{ marginTop: 0 }}>
        {ev.text}
      </p>

      {/* ---- What it rests on --------------------------------------- */}
      {evidence && (
        <section className="verdict-section">
          <h3 className="verdict-kicker">
            <span className="badge">evidence</span> What this rests on
          </h3>
          <div className="verdict-grid">
            <div>
              <div className="muted small">Milestones the verdict used</div>
              <table className="compact">
                <tbody>
                  {Object.entries(evidence.milestones).map(([kind, m]) => {
                    const cf = formatConfidence(m.confidence);
                    return (
                      <tr key={kind}>
                        <td>{milestoneName(kind)}</td>
                        <td className="mono">{formatTime(m.time_s)}</td>
                        <td className="small">{sourceLabel(m.source)}</td>
                        <td>
                          <span className={`badge ${cf.cls}`}>{cf.label}</span>
                        </td>
                        <td className="small">
                          {m.trustworthy ? (
                            <span className="badge ok">usable</span>
                          ) : (
                            <span className="badge warn">not built on</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <div className="muted small">Defects — as looked at</div>
              {Object.keys(evidence.defects).length === 0 ? (
                <p className="small muted" style={{ margin: "4px 0" }}>
                  No defect detector ran. This is not "no defects".
                </p>
              ) : (
                <ul className="plain small">
                  {Object.entries(evidence.defects).map(([kind, verdict]) => (
                    <li key={kind}>
                      {kind}:{" "}
                      <span className={`badge ${defectClass(verdict)}`}>
                        {verdict.replace(/_/g, " ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="muted small" style={{ marginTop: 8 }}>
                Peers
              </div>
              <p className="small" style={{ margin: "4px 0" }}>
                {evidence.peers.count} prior roast
                {evidence.peers.count === 1 ? "" : "s"} of this coffee,{" "}
                {evidence.peers.rated} rated
                {evidence.peers.comparable ? "" : " — too few to compare"}.
              </p>
              {evidence.peers.notes.length > 0 && (
                <ul className="plain small muted">
                  {evidence.peers.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---- What it could not see ---------------------------------- */}
      <section className="verdict-section">
        <h3 className="verdict-kicker">
          <span className="badge warn">admissions</span> What it could not see
        </h3>
        {limitations.length === 0 && ev.warnings.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }}>
            The evaluation reports no limitations for this roast.
          </p>
        ) : (
          <details open={limitations.length <= 3}>
            <summary className="small">
              {limitations.length} limitation{limitations.length === 1 ? "" : "s"}
              {ev.warnings.length > 0 &&
                `, ${ev.warnings.length} parse warning${ev.warnings.length === 1 ? "" : "s"}`}
            </summary>
            <ul className="small">
              {limitations.map((l) => (
                <li key={l}>{l}</li>
              ))}
              {ev.warnings.map((w) => (
                <li key={w} className="muted">
                  parse warning: {w}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* ---- Judgement ---------------------------------------------- */}
      <section className="verdict-section">
        <h3 className="verdict-kicker">
          <span className="badge">judgement</span> Against the plan
        </h3>
        <PlanBlock plan={ev.plan} />
        {ev.development && <DevelopmentBlock dev={ev.development} />}
        {ev.defect_flags.length > 0 && (
          <p className="small" style={{ margin: "8px 0 0" }}>
            <span className="muted">Curve defects detected: </span>
            {ev.defect_flags.map((f) => (
              <span className="badge err" key={f}>
                {f}
              </span>
            ))}
          </p>
        )}
      </section>

      {/* ---- History ------------------------------------------------- */}
      <section className="verdict-section">
        <h3 className="verdict-kicker">
          <span className="badge">comparison</span> Against your history
        </h3>
        {ev.deltas_vs_history == null ? (
          <p className="small muted" style={{ margin: 0 }}>
            No history comparison — not enough comparable prior roasts of
            this coffee.
          </p>
        ) : (
          <table className="compact">
            <tbody>
              {Object.entries(ev.deltas_vs_history).map(([k, v]) => (
                <tr key={k}>
                  <td className="muted">{k.replace(/_/g, " ")}</td>
                  <td className="mono">{signed(v)}</td>
                  <td className="small muted">vs peer median</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ---- Context ------------------------------------------------- */}
      <ContextBlock ctx={ev.context} />

      <p className="small muted" style={{ marginTop: 12, marginBottom: 0 }}>
        Temperatures in this verdict are °{ev.temp_unit}.{" "}
        <Link to={`/roasts/${encodeURIComponent(roastId)}/suggestions`}>
          What to change next time →
        </Link>
      </p>
    </div>
  );
}

function PlanBlock({ plan }: { plan: PlanAdherence | null }) {
  if (!plan || !plan.has_plan) {
    return (
      <p className="small" style={{ margin: 0 }}>
        <span className="badge warn">fallback</span>{" "}
        No planned recipe on file — judged against the generic bands, not
        against what you were aiming for.
        {plan?.reason && <span className="muted"> ({plan.reason})</span>}
      </p>
    );
  }
  return (
    <div className="col" style={{ gap: 6 }}>
      <div className="small">
        <span className="muted">Plan: </span>
        <strong>{plan.recipe_name ?? "unnamed recipe"}</strong>
        {plan.recipe_version && (
          <span className="muted mono"> v{plan.recipe_version}</span>
        )}
        {plan.headline && <span> — {plan.headline}</span>}
      </div>
      {plan.reason && <p className="small muted" style={{ margin: 0 }}>{plan.reason}</p>}
      {plan.statements.length > 0 && (
        <ul className="small" style={{ margin: 0 }}>
          {plan.statements.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      )}
      {plan.targets.length > 0 && (
        <table className="compact">
          <thead>
            <tr>
              <th>Target</th>
              <th>Planned</th>
              <th>Actual</th>
              <th>Δ</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {plan.targets.map((t) => (
              <TargetRow key={t.name} t={t} />
            ))}
          </tbody>
        </table>
      )}
      {plan.steps.length > 0 && (
        <p className="small muted" style={{ margin: 0 }}>
          {plan.steps.filter((s) => s.followed === true).length} of{" "}
          {plan.steps.length} planned control steps followed
          {plan.steps.some((s) => s.followed == null) && " (some could not be judged)"}
          .
        </p>
      )}
      {plan.end_condition?.fired && (
        <p className="small muted" style={{ margin: 0 }}>
          Ended on the {plan.end_condition.fired} condition
          {plan.end_condition.margin_note && ` — ${plan.end_condition.margin_note}`}.
        </p>
      )}
    </div>
  );
}

function TargetRow({ t }: { t: TargetDelta }) {
  const planned =
    t.planned == null
      ? "no target"
      : fmtUnit(t.planned, t.unit) +
        (t.planned_max != null && t.planned_max !== t.planned
          ? `–${fmtUnit(t.planned_max, t.unit)}`
          : "");
  return (
    <tr>
      <td>
        {t.label}
        {t.planned_source && (
          <span className="muted small"> ({t.planned_source})</span>
        )}
      </td>
      <td className="mono">{planned}</td>
      <td className="mono">{t.actual == null ? "not measured" : fmtUnit(t.actual, t.unit)}</td>
      <td className="mono">{t.delta == null ? "–" : fmtUnit(t.delta, t.unit, true)}</td>
      <td>
        {t.within_target === true && <span className="badge ok">on plan</span>}
        {t.within_target === false && <span className="badge warn">off plan</span>}
      </td>
    </tr>
  );
}

function DevelopmentBlock({ dev }: { dev: DevelopmentAssessment }) {
  const available = dev.indicators.filter((i) => i.value != null);
  return (
    <details style={{ marginTop: 8 }}>
      <summary className="small">
        Development: <strong>{dev.headline}</strong>{" "}
        <span className="muted">
          ({dev.verdict}; bands from {dev.band_origins.join(", ") || "n/a"})
        </span>
      </summary>
      <table className="compact">
        <thead>
          <tr>
            <th>Indicator</th>
            <th>Value</th>
            <th>Band</th>
            <th>Origin</th>
            <th>Read</th>
          </tr>
        </thead>
        <tbody>
          {available.map((i) => (
            <tr key={i.name}>
              <td>
                {i.label}
                {i.marker_dependent && (
                  <span className="muted small" title="Depends on a milestone marker"> ·marker</span>
                )}
              </td>
              <td className="mono">
                {i.value}
                {i.unit}
              </td>
              <td className="mono small">
                {i.band_low ?? "–"}–{i.band_high ?? "–"}
                {i.unit}
              </td>
              <td className="small muted">{i.band_origin}</td>
              <td>
                <span className={`badge ${i.confidence >= 0.5 ? "" : "warn"}`}>
                  {i.position ?? "–"}
                  {i.confidence < 0.5 && " · not trusted"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {dev.reasons.length > 0 && (
        <ul className="small muted">
          {dev.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </details>
  );
}

function ContextBlock({ ctx }: { ctx: EvaluationContext }) {
  const facts: string[] = [];
  if (ctx.position_in_session != null) {
    facts.push(
      `roast ${ctx.position_in_session} of ${ctx.session_size ?? "?"} in the session`,
    );
  }
  if (ctx.ambient_temp != null) facts.push(`ambient ${ctx.ambient_temp} °C`);
  if (ctx.humidity_pct != null) facts.push(`humidity ${ctx.humidity_pct}%`);
  if (ctx.pressure_hpa != null) facts.push(`pressure ${ctx.pressure_hpa} hPa`);
  const effects = ctx.effects ?? [];
  if (facts.length === 0 && effects.length === 0) return null;
  return (
    <section className="verdict-section">
      <h3 className="verdict-kicker">
        <span className="badge">context</span> Conditions — reported, never
        corrected for
      </h3>
      {facts.length > 0 && (
        <p className="small" style={{ margin: 0 }}>
          {facts.join(" · ")}
        </p>
      )}
      {effects.length > 0 && (
        <ul className="small muted" style={{ margin: "4px 0 0" }}>
          {effects.map((e) => (
            <li key={`${e.factor}-${e.metric}`}>{e.text}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function defectClass(verdict: string): "ok" | "warn" | "err" | "" {
  if (verdict === "detected") return "err";
  if (verdict === "not_detected") return "ok";
  if (verdict === "not_evaluated") return "warn";
  return "";
}

function fmtUnit(v: number, unit: string, signedOut = false): string {
  if (unit === "s") {
    const s = formatTime(Math.abs(v));
    return signedOut ? `${v < 0 ? "−" : "+"}${s}` : s;
  }
  const n = Math.abs(v) >= 10 ? v.toFixed(0) : v.toFixed(1);
  const sign = signedOut && v > 0 ? "+" : "";
  return `${sign}${n}${unit === "%" ? "%" : ` ${unit}`}`;
}

function signed(v: number): string {
  const n = Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1);
  return v > 0 ? `+${n}` : n;
}
